import moment from "moment";
import { useIntl } from "react-intl";

import {
  TypographyVariants,
  Typography
} from "@certa/catalyst/components/Typography";
import { Stack } from "@certa/catalyst/layouts/Stack";
import { Dialog } from "@certa/catalyst/components/Dialog";
import { CatalystColors } from "@certa/catalyst/constants/styles";
import { Tag } from "@certa/catalyst/components/Tag";
import { showToast, ToastTypes } from "@certa/catalyst/components/Toast";

import {
  useCreateMassDelegation,
  type Delegatee
} from "@certa/queries/queries/massDelegation/massDelegation.query";

import { getCommonDelegationUserAccess } from "./ManageDelegations.utils";
import { useAuth } from "@certa/auth/AuthenticationProvider";

export type SubmittedDelegation = {
  id?: number;
  delegationName: string;
  selectDelegate: string;
  startDate: string;
  endDate: string;
  reasonNotes: string;
};

type ConfirmDelegationProps = {
  canShow: boolean;
  onCancel: () => void;
  delegatedUser: Delegatee["results"][number];
  submittedDelegation: SubmittedDelegation;
  onClose: () => void;
  isUpdatingDelegation: boolean;
  onUpdateDelegation: (value: SubmittedDelegation) => void;
  onCreationSuccess: () => void;
};

export const ConfirmDelegation = (props: ConfirmDelegationProps) => {
  const {
    canShow,
    onClose,
    delegatedUser,
    submittedDelegation,
    onCancel,
    isUpdatingDelegation,
    onUpdateDelegation,
    onCreationSuccess
  } = props;

  const { user } = useAuth();

  const {
    groups,
    regionFullAccess,
    regionReadAccess,
    businessUnitFullAccess,
    businessUnitReadAccess
  } = getCommonDelegationUserAccess(user, delegatedUser);

  const {
    mutate: createMassDelegationMutation,
    isLoading: isCreatingDelegation
  } = useCreateMassDelegation({
    onSuccess: () => {
      showToast({
        title: intl.formatMessage({
          id: "delegations.delegationCreatedSuccessfully",
          defaultMessage: "Delegation created successfully"
        }),
        type: ToastTypes.SUCCESS
      });
      onCreationSuccess();
    },
    onError: async (error: Response) => {
      let errorMessage = intl.formatMessage({
        id: "delegations.failedToCreateDelegation",
        defaultMessage: "Failed to create delegation"
      });

      try {
        const errorData = await error.json();

        if (Array.isArray(errorData)) {
          errorMessage = errorData[0];
        } else if (typeof errorData === "object") {
          errorMessage = Object.values(errorData)[0] as string;
        }
      } catch (error) {
        console.error(error);
      }

      showToast({
        title: errorMessage,
        type: ToastTypes.ERROR
      });
      console.error(error);
    }
  });

  const isLoading = isCreatingDelegation || isUpdatingDelegation;

  const confirmDelegation = () => {
    if (!submittedDelegation) {
      return;
    }

    if (submittedDelegation?.id) {
      onUpdateDelegation(submittedDelegation);
    } else {
      const payload = {
        delegation_id: submittedDelegation?.id,
        name: submittedDelegation.delegationName,
        delegate_future_tasks: true,
        delegate_in_progress_tasks: true,
        start_date: moment(submittedDelegation.startDate).format("YYYY-MM-DD"),
        end_date: moment(submittedDelegation.endDate).format("YYYY-MM-DD"),
        user_email: submittedDelegation.selectDelegate,
        reason: submittedDelegation.reasonNotes
      };
      createMassDelegationMutation(payload);
    }
  };

  const intl = useIntl();

  const confirmDelegationButtonLabel = intl.formatMessage({
    id: "commonTextInstances.confirm",
    defaultMessage: "Confirm"
  });

  const backButtonLabel = intl.formatMessage({
    id: "commonTextInstances.back",
    defaultMessage: "Back"
  });

  const confirmDelegationTitle = intl.formatMessage({
    id: "confirmDelegation.title",
    defaultMessage: "Confirm Delegation"
  });

  const confirmDelegationDescription = intl.formatMessage(
    {
      id: "confirmDelegation.description",
      defaultMessage:
        "{email} will be notified and will be able to see a list of delegated tasks in the tasks page. They can complete tasks they have access to."
    },
    {
      email: delegatedUser?.email
    }
  );

  const confirmDelegationPermissionsLabel = intl.formatMessage({
    id: "confirmDelegation.permissions",
    defaultMessage: "Below are the permissions you have in common:"
  });

  const regionFullAccessLabel = intl.formatMessage({
    id: "confirmDelegation.regionFullAccess",
    defaultMessage: "Region with full access"
  });

  const regionReadAccessLabel = intl.formatMessage({
    id: "confirmDelegation.regionReadAccess",
    defaultMessage: "Region with read only access"
  });

  const businessUnitFullAccessLabel = intl.formatMessage({
    id: "confirmDelegation.businessUnitFullAccess",
    defaultMessage: "Business Units with full access"
  });

  const businessUnitReadAccessLabel = intl.formatMessage({
    id: "confirmDelegation.businessUnitReadAccess",
    defaultMessage: "Business Units with read only access"
  });

  return (
    <Dialog
      show={canShow}
      width="40%"
      maxHeight="60%"
      onClose={onClose}
      primaryActionText={confirmDelegationButtonLabel}
      onPrimaryAction={confirmDelegation}
      primaryButtonProps={{
        disabled: isLoading,
        loading: isLoading
      }}
      secondaryButtonProps={{
        disabled: isLoading
      }}
      showCloseIcon={!isLoading}
      secondaryActionText={backButtonLabel}
      onSecondaryAction={onCancel}
      preventCloseOnClickOutside={isLoading}
      title={confirmDelegationTitle}
      description={confirmDelegationDescription}
    >
      <Stack gap="16">
        <Typography
          variant={TypographyVariants.LABEL_SM}
          color={CatalystColors.NEUTRAL_700}
        >
          {confirmDelegationPermissionsLabel}
        </Typography>
        <ConfirmDelegationFieldReview children="User Groups" options={groups} />
        <ConfirmDelegationFieldReview
          children={regionFullAccessLabel}
          options={regionFullAccess}
        />
        <ConfirmDelegationFieldReview
          children={regionReadAccessLabel}
          options={regionReadAccess}
        />
        <ConfirmDelegationFieldReview
          children={businessUnitFullAccessLabel}
          options={businessUnitFullAccess}
        />
        <ConfirmDelegationFieldReview
          children={businessUnitReadAccessLabel}
          options={businessUnitReadAccess}
        />
      </Stack>
    </Dialog>
  );
};

type ConfirmDelegationFieldReviewProps = {
  children: string;
  options: { id?: number; name?: string }[];
};

const ConfirmDelegationFieldReview = (
  props: ConfirmDelegationFieldReviewProps
) => {
  const { children, options = [] } = props;

  return (
    <Stack gap="8">
      <Typography
        variant={TypographyVariants.LABEL_SM}
        color={CatalystColors.NEUTRAL_800}
      >
        {children}
      </Typography>
      <Stack direction="horizontal" gap="8" width="100%" wrap="wrap">
        {options.length > 0 ? (
          options.map(option => {
            return option.name ? (
              <Tag key={option.id} label={option.name} multiline={true}>
                {option.name}
              </Tag>
            ) : null;
          })
        ) : (
          <Typography variant={TypographyVariants.BODY_BOLD}>N/A</Typography>
        )}
      </Stack>
    </Stack>
  );
};
